<template>
  <div>
    <aside v-if="!ready" class="app-loading">
      <p>Loading Online Scheduling...</p>
    </aside>
    <aside v-if="ready && clinic" class="onlineScheduling">
      <div class="content">
        <div class="onlineScheduling__main">
          <span class="onlineScheduling__header">Schedule an Appointment in <strong>{{ clinic.name }}</strong></span>
          <button class="btn-beltone onlineScheduling__changeLocations" @click="changeLocations()">
            <IconMapMarker/>
            <span>Change Locations</span>
          </button>
          <div class="onlineScheduling__steps">
            <div v-for="s in steps"
                 :key="s.step"
                 class="onlineScheduling__step"
                 :class="[getStepState(s.step), {'is-clickable': canNavigateToStep(s.step)}]"
                 :tabindex="canNavigateToStep(s.step) ? 0 : null"
                 :role="canNavigateToStep(s.step) ? 'button' : null"
                 @click="goToStep(s.step)"
                 @keydown.enter.prevent="goToStep(s.step)"
                 @keydown.space.prevent="goToStep(s.step)">
              <component :is="s.icon" class="onlineScheduling__step-icon" :colorState="getStepState(s.step)"/>
              <label class="onlineScheduling__step-header">Step {{ s.step }}</label>
              <label class="onlineScheduling__step-copy">
                <span class="onlineScheduling__step-copy-full">{{ s.label }}</span>
                <span class="onlineScheduling__step-copy-short">{{ s.shortLabel }}</span>
              </label>
              <div class="onlineScheduling__step-bar"></div>
            </div>
          </div>
        </div>
        <step-1 v-if="step === 1"/>
        <step-2 v-if="step === 2" :clinicId="clinic.id" :dealerId="dealerId"/>
        <step-3
          v-if="step === 3"
          :dealerId="dealerId"
          :hide-sms-consent="shouldHideSmsConsent"/>
        <step-4 v-if="step === 4" :dealerId="dealerId"/>
      </div>
    </aside>
  </div>
</template>

<script>
import {mapState, mapActions, mapMutations} from 'vuex';
import Services from '../mixins/services';
import Step1 from './../components/online-scheduling/step-1.vue';
import Step2 from './../components/online-scheduling/step-2.vue';
import Step3 from '../components/online-scheduling/step-3.vue';
import Step4 from '../components/online-scheduling/step-4.vue';
import IconIconMapMarker from '../icons/icon-map-marker.vue';

import IconIconStep1 from '../icons/icon-step1.vue';
import IconIconStep2 from '../icons/icon-step2.vue';
import IconIconStep3 from '../icons/icon-step3.vue';
import IconIconStep4 from '../icons/icon-step4.vue';
import { isBeltoneMichiganDealer } from '../dealer-constants';
import scrollPageToTop from '../scroll-page-to-top';

export default {
  mixins: [Services],
  props: {
    dealerId: {
      type: String,
      required: true,
    }
  },
  components: {
    IconMapMarker: IconIconMapMarker,
    IconStep1: IconIconStep1,
    IconStep2: IconIconStep2,
    IconStep3: IconIconStep3,
    IconStep4: IconIconStep4,
    Step1,
    Step2,
    Step3,
    Step4,
  },
  data: function () {
    return {
      ready: false,
      steps: [
        { step: 1, label: 'Appointment Type', shortLabel: 'Type', icon: 'IconStep1' },
        { step: 2, label: 'Date & Time', shortLabel: 'Datetime', icon: 'IconStep2' },
        { step: 3, label: 'Patient Details', shortLabel: 'Details', icon: 'IconStep3' },
        { step: 4, label: 'Confirmation', shortLabel: 'Confirm', icon: 'IconStep4' }
      ]
    }
  },
  computed: {
    ...mapState({
      step: state => state.OnlineScheduling.step,
      clinic: state => state.OnlineScheduling.clinic,
      clinicUrl: state => state.OnlineScheduling.clinicUrl,
      globals: state => state.OnlineScheduling.globals,
      clientToken: state => state.ClientToken.clientToken,
    }),
    shouldHideSmsConsent() {
      return isBeltoneMichiganDealer(this.dealerId);
    },
  },
  methods: {
    ...mapActions('ClientToken', {getClientToken: 'getClientToken'}),
    ...mapActions('OnlineScheduling', {getClinicInformation: 'getClinicInformation'}),
    ...mapActions('OnlineScheduling', {getStaff: 'getStaff'}),
    ...mapActions('OnlineScheduling', {getCompanionTypes: 'getCompanionTypes'}),
    ...mapMutations('OnlineScheduling', { setStateValue: 'setStateValue' }),
    getStepState(step) {
      if (this.step === step) {
        return 'focus';
      } else if (this.step > step) {
        return 'complete';
      } else if (this.step < step) {
        return 'inactive';
      }
    },
    canNavigateToStep(step) {
      return step <= this.step;
    },
    goToStep(step) {
      if (!this.canNavigateToStep(step)) return;

      this.setStateValue({ key: 'step', value: step });
    },
    changeLocations() {
      window.location.href = '/locations';
    },
    shouldUsePreferredStaffFlow() {
      return isBeltoneMichiganDealer(this.dealerId);
    },
    init() {
      this.getClientToken({
        cb: () => {
          this.getClinicInformation({
            clinicUrl: this.clinicUrl,
            dealerId: this.dealerId,
            clientToken: this.clientToken,
            cb: (clinic) => {
              if (!clinic) {
                this.ready = false;
                return;
              }

              if (!this.shouldUsePreferredStaffFlow()) {
                this.getCompanionTypes({
                  dealerId: this.dealerId,
                  clientToken: this.clientToken,
                  cb: () => {
                    this.ready = true;
                  }
                });
                return;
              }

              this.getStaff({
                clinicId: clinic.id,
                dealerId: this.dealerId,
                clientToken: this.clientToken,
                cb: () => {
                  this.getCompanionTypes({
                    dealerId: this.dealerId,
                    clientToken: this.clientToken,
                    cb: () => {
                      this.ready = true;
                    }
                  });
                }
              });
            }
          })
        }
      });
    }
  },
  mounted () {
    scrollPageToTop();

    if (!this.clinicUrl) {
      const path = window.location.pathname;
      const segments = path.split('/').filter(Boolean);
      
      let clinicUrl = segments[segments.length - 1];
      
      // Special edge case for Fayetteville Arkansas Hearing Care Center
      if (clinicUrl === 'fayetteville-arkansas-hearing-care-center') {
        clinicUrl = 'fayetteville';
      }

      this.setStateValue({ key: 'clinicUrl', value: clinicUrl });
    }
    this.init();
  }
    
}
</script>
